import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Lock, LayoutDashboard, PlusCircle, ArrowLeft, LogOut, GitCompareArrows } from "lucide-react";
import { hasCIAccess, verifyCICode, clearCIAccess } from "./ciApi";

const NAV = [
  { to: "/company-intelligence/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/company-intelligence/new", label: "New Assessment", Icon: PlusCircle },
  { to: "/company-intelligence/compare", label: "Compare", Icon: GitCompareArrows },
];

const CIGate = ({ onUnlock }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verifyCICode(code.trim());
      onUnlock();
    } catch {
      setError("Invalid access code. This module is restricted to SAMANVAI administration.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060d18] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-teal-300 mb-4 text-center">
          SAMANVAI · Company Intelligence
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0c1626] p-8">
          <div className="w-11 h-11 rounded-full bg-teal-400/10 border border-teal-400/25 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-5 h-5 text-teal-300" />
          </div>
          <h1 className="font-serif text-white text-2xl text-center mb-2">Restricted access</h1>
          <p className="text-sm text-white/55 text-center mb-6 leading-relaxed">
            The Company Intelligence suite is currently in administrator preview. Enter your access code to continue.
          </p>
          <form onSubmit={submit}>
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Access code"
              data-testid="ci-gate-code-input"
              className="w-full rounded-lg bg-[#060d18] border border-white/15 px-4 py-3 text-white text-center tracking-[0.4em] focus:outline-none focus:border-teal-400/60"
            />
            {error && (
              <div className="text-xs text-red-300 mt-3 text-center" data-testid="ci-gate-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="ci-gate-submit-btn"
              className="w-full mt-5 rounded-lg bg-teal-400 hover:bg-teal-300 disabled:opacity-40 text-[#06251f] font-bold text-sm tracking-wide py-3 transition-colors"
            >
              {busy ? "Verifying…" : "Unlock the suite"}
            </button>
          </form>
          <Link to="/company-intelligence" className="block text-center text-xs text-white/40 hover:text-white/70 mt-5 transition-colors">
            ← Back to the offering overview
          </Link>
        </div>
      </div>
    </div>
  );
};

export const CIShell = () => {
  const [unlocked, setUnlocked] = useState(hasCIAccess());
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!unlocked) return <CIGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-[#060d18] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#060d18]/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center gap-8">
          <Link to="/company-intelligence" className="flex items-baseline gap-2 shrink-0" data-testid="ci-shell-wordmark">
            <span className="font-serif text-lg text-white">SAMANVAI</span>
            <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-teal-300">Company Intelligence</span>
          </Link>
          <nav className="flex items-center gap-1 flex-1">
            {NAV.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                data-testid={`ci-nav-${label.toLowerCase().replace(/ /g, "-")}`}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                  pathname === to ? "bg-teal-400/10 text-teal-300 border border-teal-400/25" : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-white/45 hover:text-white/80 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> SAMANVAI home
            </Link>
            <button
              onClick={() => {
                clearCIAccess();
                navigate("/company-intelligence");
              }}
              data-testid="ci-shell-lock-btn"
              className="inline-flex items-center gap-1.5 text-[12px] text-white/45 hover:text-amber-300 px-2 py-1 transition-colors"
              title="Lock the suite"
            >
              <LogOut className="w-3.5 h-3.5" /> Lock
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
};
